'use client';

import React, { ReactElement, useMemo } from 'react';
import { Currency } from 'utils/Currency';
import { CurrencySymbol } from 'hooks/pricing/useFormattedPrice';
import { getCurrencySymbolFromString } from 'lib/region/countryUtils';

/**
 * Safely get currency symbol React element by currency code
 */
export const getCurrencySymbolElement = (currencyCode: string): ReactElement => {
  const normalizedCode = currencyCode.toLowerCase();
  return CurrencySymbol[normalizedCode as Currency] || React.createElement(React.Fragment, null, '$');
};

/**
 * Get locale code based on currency
 */
export const getLocaleFromCurrency = (currency: Currency): string => {
  switch (currency) {
    case Currency.inr:
      return 'en-IN';
    case Currency.gbp:
      return 'en-GB';
    case Currency.eur:
      return 'de-DE'; // Using German as representative for Euro
    case Currency.aed:
      return 'ar-AE';
    case Currency.jpy:
      return 'ja-JP';
    case Currency.usd:
    default:
      return 'en-US';
  }
};

/**
 * Format a number according to Indian numbering system (lakhs, crores)
 */
const formatIndianSystem = (price: number): string => {
  // For very large numbers, use crores (10^7)
  if (price >= 10000000) {
    return `${(price / 10000000).toFixed(price % 10000000 === 0 ? 0 : 2)} Cr`;
  }

  // For large numbers, use lakhs (10^5)
  if (price >= 100000) {
    return `${(price / 100000).toFixed(price % 100000 === 0 ? 0 : 2)} L`;
  }

  // Otherwise use the Indian locale with thousands separators
  return new Intl.NumberFormat('en-IN').format(price);
};

/**
 * Format price according to locale and currency
 * This is a pure utility function (not a hook) that can be called anywhere
 */
export const formatPrice = (
  price: number,
  currency: Currency | string = Currency.usd,
  options: Intl.NumberFormatOptions = {}
): string => {
  // Normalize currency if it's a string
  const currencyEnum = typeof currency === 'string'
    ? currency.toLowerCase() as Currency
    : currency;

  // Special case for Indian Rupee using Indian numbering system
  if (currencyEnum === Currency.inr) {
    return formatIndianSystem(price);
  }

  // Special case for AED which doesn't use decimal places typically
  if (currencyEnum === Currency.aed) {
    return new Intl.NumberFormat(getLocaleFromCurrency(currencyEnum), {
      maximumFractionDigits: 0,
      ...options
    }).format(price);
  }

  // For other currencies, use standard formatting
  return new Intl.NumberFormat(getLocaleFromCurrency(currencyEnum), {
    style: options.style || 'decimal',
    maximumFractionDigits: options.maximumFractionDigits ?? 0,
    ...options
  }).format(price);
};

/**
 * Format price with currency symbol
 */
export const formatPriceWithSymbol = (
  price: number,
  currency: Currency | string = Currency.usd,
  options: Intl.NumberFormatOptions = {}
): string => {
  // Normalize currency if it's a string
  const currencyEnum = typeof currency === 'string'
    ? currency.toLowerCase() as Currency
    : currency;

  const symbol = getCurrencySymbolFromString(currencyEnum);
  const formattedPrice = formatPrice(price, currencyEnum, options);

  // Special case for AED which displays symbol after the amount
  if (currencyEnum === Currency.aed) {
    return `${formattedPrice} ${symbol}`;
  }

  return `${symbol}${formattedPrice}`;
};

/**
 * Create a price display element with proper currency symbol placement
 * This is a pure utility function (not a hook) that can be called anywhere
 */
export const createPriceDisplay = (
  price: number,
  currency: Currency | string = Currency.usd,
  options: Intl.NumberFormatOptions = {}
): React.ReactNode => {
  // Normalize currency if it's a string
  const currencyEnum = typeof currency === 'string'
    ? currency.toLowerCase() as Currency
    : currency;

  const formatted = formatPrice(price, currencyEnum, options);
  const symbol = getCurrencySymbolElement(currencyEnum);

  if (currencyEnum === Currency.aed) {
    return React.createElement(React.Fragment, null, [
      formatted,
      ' ',
      React.cloneElement(symbol, { key: 'symbol' })
    ]);
  }

  return React.createElement(React.Fragment, null, [
    React.cloneElement(symbol, { key: 'symbol' }),
    ' ',
    formatted
  ]);
};

/**
 * Hook for formatting prices based on currency
 * Returns formatted prices and utility functions
 * This is a proper React hook that must follow React hook rules
 */
export const useFormattedPriceNew = (
  price: number,
  currency: Currency | string = Currency.usd,
  options: Intl.NumberFormatOptions = {}
) => {
  return useMemo(() => {
    // Normalize currency if it's a string
    const currencyEnum = typeof currency === 'string'
      ? currency.toLowerCase() as Currency
      : currency;

    const formatted = formatPrice(price, currencyEnum, options);
    const symbol = getCurrencySymbolElement(currencyEnum);
    const display = createPriceDisplay(price, currencyEnum, options);

    // Function to format other prices with the same currency
    const format = (p: number) => formatPrice(p, currencyEnum, options);

    // Function to create display elements for other prices with the same currency
    const createDisplay = (p: number) => createPriceDisplay(p, currencyEnum, options);

    return {
      formatted,
      symbol,
      display,
      format,
      createDisplay,
      // Include raw value for calculations
      raw: price,
      // Include currency for reference
      currency: currencyEnum
    };
  }, [price, currency, options]);
};

export default useFormattedPriceNew;
